import { useNavigate } from "@tanstack/react-router";
import { useId } from "react";
import type { PageMeta } from "@/api/paging";
import { secondaryButtonClass } from "@/components/ui";

export const PAGE_SIZES = [25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

const count = (n: number) => n.toLocaleString("en-NG");
const buttonClass = `min-h-10 rounded-md border px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 md:min-h-0 ${secondaryButtonClass}`;

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
  className = "",
}: {
  className?: string;
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize: (size: PageSize) => void;
}) {
  const sizeId = useId();
  if (total <= pageSize && page === 1) return null;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const first = (page - 1) * pageSize + 1;
  const range = first > total ? `Showing 0 of ${count(total)}` : `Showing ${count(first)}–${count(Math.min(page * pageSize, total))} of ${count(total)}`;

  return (
    <nav aria-label="Pagination" className={`flex flex-wrap items-center justify-between gap-3 text-sm text-zinc-600 ${className}`.trim()}>
      <p>{range}</p>
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor={sizeId} className="flex items-center gap-2">
          Rows per page
          <select
            id={sizeId}
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value) as PageSize)}
            className="min-h-10 rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm text-zinc-800 focus:border-brand md:min-h-0"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <span>
          Page {count(page)} of {count(lastPage)}
        </span>
        {/* Past the end, Previous lands on the last real page. */}
        <button type="button" className={buttonClass} disabled={page <= 1} onClick={() => onPage(Math.min(page - 1, lastPage))}>
          Previous
        </button>
        <button type="button" className={buttonClass} disabled={page >= lastPage} onClick={() => onPage(page + 1)}>
          Next
        </button>
      </div>
    </nav>
  );
}

/** Pagination for a list whose page and page_size live in the current route's URL. */
export function PagedFooter({
  data,
  page,
  pageSize,
  className = "border-t border-zinc-200 px-4 py-3",
}: {
  data: PageMeta | undefined;
  page: number | undefined;
  pageSize: PageSize | undefined;
  className?: string;
}) {
  const navigate = useNavigate();
  if (!data) return null;
  return (
    <Pagination
      className={className}
      page={page ?? 1}
      pageSize={pageSize ?? PAGE_SIZES[0]}
      total={data.total}
      onPage={(next) => void navigate({ to: ".", search: (prev) => ({ ...prev, page: next }) })}
      onPageSize={(size) => void navigate({ to: ".", search: (prev) => ({ ...prev, page: undefined, page_size: size }) })}
    />
  );
}
